import { inject } from '@angular/core';
import { CanActivateChildFn, Router } from '@angular/router';
import { AccessService } from '../services/access/access.service';
import { afterAccessLoaded } from './access.guard';

/** Pantalla donde el administrador de una cuenta en prueba acepta los términos. */
export const TRIAL_TERMS_URL = '/app/billing/terms';

/**
 * ¿Debe esta persona aceptar los términos de la prueba antes de usar la app? Sólo quien puede
 * ver la suscripción (el administrador): el API rechaza la aceptación de cualquier otro, y los
 * términos son de la empresa, no de cada usuario. Tampoco en modo consulta ni con la cuenta
 * bloqueada (término de la suscripción): el API la rechaza (`COMPANY_READ_ONLY`) y la pantalla
 * dejaría al administrador sin su período de consulta.
 */
export function trialTermsRequired(access: AccessService): boolean {
  const state = access.companyAccess()?.state;
  if (state === 'read_only' || state === 'blocked') return false;
  return access.trial()?.termsPending === true && access.can('subscription.view');
}

/** ¿La URL (con o sin query) es la pantalla de términos? */
export function isTrialTermsUrl(url: string): boolean {
  return url.split(/[?#]/)[0] === TRIAL_TERMS_URL;
}

/**
 * Primer ingreso de una cuenta en prueba: mientras `/access/me` diga que los términos y el aviso
 * de certificación están pendientes, el administrador no usa la app hasta aceptarlos (decisión
 * del diseño «contratación en prueba»). Va en `canActivateChild` del shell DESPUÉS de
 * `accessGuard`, que conserva la prioridad: si no puede abrir la ruta, gana su `sin-acceso`.
 * Guarda la ruta pedida en `from` para volver a ella al aceptar.
 */
export const trialTermsGuard: CanActivateChildFn = (_route, state) => {
  const router = inject(Router);
  const access = inject(AccessService);
  return afterAccessLoaded(() =>
    trialTermsRequired(access) && !isTrialTermsUrl(state.url)
      ? router.createUrlTree([TRIAL_TERMS_URL], { queryParams: { from: state.url } })
      : true,
  );
};
